import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';
import { GeneralQuestionCategory } from './questions.enum';
import { AvailableDealDto, DealQuestionDto, EscalateQuestionPayload, GeneralQuestionDto, PostQuestionAnswerPayload, QuestionReplyDto, QuestionsListQuery, RaiseDealQuestionPayload, RaiseGeneralQuestionPayload } from './questions.model';

@Injectable({
    providedIn: 'root'
})
export class QuestionsApiService {
    private readonly apiUrl = environment.urls.api;

    private readonly _httpClient = inject(HttpClient);

    /** GET /api/v1/Deals/questions — one page of the deal questions visible to the caller. */
    public getDealQuestions(query: QuestionsListQuery): Observable<PaginatedResponse<DealQuestionDto>> {
        return this._httpClient.get<PaginatedResponse<DealQuestionDto>>(`${this.apiUrl}/Deals/questions`, { params: this.buildListParams(query) });
    }

    /** GET /api/v1/Deals/question/{questionId} — one thread. Note the singular `question` segment, unlike every other deal-question route. */
    public getDealQuestionById(questionId: number): Observable<DealQuestionDto | null> {
        return this._httpClient.get<DealQuestionDto | null>(`${this.apiUrl}/Deals/question/${questionId}`);
    }

    /** GET /api/v1/Deals/questions/available-deals — the deals this caller may raise a question about. */
    public getAvailableDeals(): Observable<AvailableDealDto[]> {
        return this._httpClient.get<AvailableDealDto[]>(`${this.apiUrl}/Deals/questions/available-deals`);
    }

    /** POST /api/v1/Deals/questions/{dealId}/raise */
    public raiseDealQuestion(dealId: number, payload: RaiseDealQuestionPayload): Observable<DealQuestionDto> {
        return this._httpClient.post<DealQuestionDto>(`${this.apiUrl}/Deals/questions/${dealId}/raise`, payload);
    }

    /** POST /api/v1/Deals/questions/{questionId}/answers */
    public answerDealQuestion(questionId: number, payload: PostQuestionAnswerPayload): Observable<QuestionReplyDto> {
        return this._httpClient.post<QuestionReplyDto>(`${this.apiUrl}/Deals/questions/${questionId}/answers`, payload);
    }

    /**
     * POST /api/v1/Deals/{dealId}/questions/{questionId}/escalate
     *
     * The deal id sits outside the `/questions/` segment here, unlike every other deal-question
     * route, and this endpoint returns the real HTTP status on failure rather than a 200 with
     * `success: false`.
     */
    public escalateDealQuestion(dealId: number, questionId: number, payload: EscalateQuestionPayload): Observable<DealQuestionDto> {
        return this._httpClient.post<DealQuestionDto>(`${this.apiUrl}/Deals/${dealId}/questions/${questionId}/escalate`, payload);
    }

    /** PATCH /api/v1/Deals/questions/{questionId}/accept-answer — original asker only; 404s for anyone else. */
    public acceptDealQuestionAnswer(questionId: number): Observable<DealQuestionDto> {
        return this._httpClient.patch<DealQuestionDto>(`${this.apiUrl}/Deals/questions/${questionId}/accept-answer`, {});
    }

    /** GET /api/v1/GeneralQuestions — one page, optionally narrowed to a single category. */
    public getGeneralQuestions(query: QuestionsListQuery, category?: GeneralQuestionCategory | null): Observable<PaginatedResponse<GeneralQuestionDto>> {
        return this._httpClient.get<PaginatedResponse<GeneralQuestionDto>>(`${this.apiUrl}/GeneralQuestions`, { params: this.buildListParams(query, { category }) });
    }

    /** GET /api/v1/GeneralQuestions/{questionId} — one thread. */
    public getGeneralQuestionById(questionId: number): Observable<GeneralQuestionDto | null> {
        return this._httpClient.get<GeneralQuestionDto | null>(`${this.apiUrl}/GeneralQuestions/${questionId}`);
    }

    /** POST /api/v1/GeneralQuestions/raise */
    public raiseGeneralQuestion(payload: RaiseGeneralQuestionPayload): Observable<GeneralQuestionDto> {
        return this._httpClient.post<GeneralQuestionDto>(`${this.apiUrl}/GeneralQuestions/raise`, payload);
    }

    /** POST /api/v1/GeneralQuestions/{questionId}/answers */
    public answerGeneralQuestion(questionId: number, payload: PostQuestionAnswerPayload): Observable<QuestionReplyDto> {
        return this._httpClient.post<QuestionReplyDto>(`${this.apiUrl}/GeneralQuestions/${questionId}/answers`, payload);
    }

    /** POST /api/v1/GeneralQuestions/{questionId}/escalate — returns the real HTTP status on failure. */
    public escalateGeneralQuestion(questionId: number, payload: EscalateQuestionPayload): Observable<GeneralQuestionDto> {
        return this._httpClient.post<GeneralQuestionDto>(`${this.apiUrl}/GeneralQuestions/${questionId}/escalate`, payload);
    }

    /** PATCH /api/v1/GeneralQuestions/{questionId}/accept-answer — original asker only. */
    public acceptGeneralQuestionAnswer(questionId: number): Observable<GeneralQuestionDto> {
        return this._httpClient.patch<GeneralQuestionDto>(`${this.apiUrl}/GeneralQuestions/${questionId}/accept-answer`, {});
    }

    private buildListParams(query: QuestionsListQuery, extra: Record<string, number | string | null | undefined> = {}): HttpParams {
        return this.buildParams({
            Page: query.page,
            PageSize: query.pageSize,
            status: query.status,
            createdByRole: query.createdByRole,
            ...extra
        });
    }

    private buildParams(values: Record<string, number | string | null | undefined>): HttpParams {
        let params = new HttpParams();
        for (const [key, value] of Object.entries(values)) {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        }
        return params;
    }
}
